import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Empty, Spin, Typography } from 'antd'
import { getDocument, GlobalWorkerOptions, type PDFDocumentProxy } from 'pdfjs-dist'
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import {
  locateQuoteInPdf,
  scaleHighlightRects,
  type PdfHighlightRect,
} from '../lib/pdfQuoteHighlight'
import './PdfQuoteHighlightViewer.css'

GlobalWorkerOptions.workerSrc = pdfWorker

const RENDER_SCALE = 1.35
const IO_ROOT_MARGIN = '480px 0px'

type PdfQuoteHighlightViewerProps = {
  blobUrl: string | null
  pageNo?: number | null
  quote?: string | null
  className?: string
  minHeight?: string
}

type LocateState = {
  pageNumber: number
  highlights: PdfHighlightRect[]
  matched: boolean
}

type PageSize = {
  width: number
  height: number
}

type OverlaySize = {
  width: number
  height: number
}

const PdfLazyPage = memo(function PdfLazyPage({
  pdf,
  pageNumber,
  highlights,
  onRendered,
}: {
  pdf: PDFDocumentProxy
  pageNumber: number
  highlights: PdfHighlightRect[]
  onRendered?: (size: PageSize) => void
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [rendering, setRendering] = useState(true)
  const [size, setSize] = useState<PageSize | null>(null)
  const [overlaySize, setOverlaySize] = useState<OverlaySize | null>(null)
  const [scaledHighlights, setScaledHighlights] = useState<PdfHighlightRect[]>(highlights)

  const syncHighlightOverlay = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || canvas.width <= 0 || canvas.height <= 0) return
    const rect = canvas.getBoundingClientRect()
    if (rect.width <= 0 || rect.height <= 0) return
    setOverlaySize({ width: rect.width, height: rect.height })
    setScaledHighlights(
      scaleHighlightRects(highlights, rect.width / canvas.width, rect.height / canvas.height),
    )
  }, [highlights])

  useEffect(() => {
    if (!highlights.length) {
      setScaledHighlights([])
      return
    }
    syncHighlightOverlay()
  }, [highlights, syncHighlightOverlay])

  useEffect(() => {
    let cancelled = false
    setRendering(true)

    void (async () => {
      try {
        const page = await pdf.getPage(pageNumber)
        if (cancelled) return
        const viewport = page.getViewport({ scale: RENDER_SCALE })
        const nextSize = { width: viewport.width, height: viewport.height }
        setSize(nextSize)
        onRendered?.(nextSize)

        const canvas = canvasRef.current
        if (!canvas || cancelled) return
        canvas.width = viewport.width
        canvas.height = viewport.height
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        await page.render({ canvasContext: ctx, viewport }).promise
        if (!cancelled) {
          setRendering(false)
          requestAnimationFrame(() => syncHighlightOverlay())
        }
      } catch {
        if (!cancelled) setRendering(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [pdf, pageNumber, onRendered, syncHighlightOverlay])

  return (
    <div
      className="pdf-quote-page"
      style={
        size
          ? { width: size.width, maxWidth: '100%' }
          : { minHeight: 420, width: '100%', maxWidth: 860 }
      }
    >
      {rendering ? (
        <div className="pdf-quote-page-loading">
          <Spin size="small" />
        </div>
      ) : null}
      <canvas ref={canvasRef} />
      {overlaySize && scaledHighlights.length > 0 ? (
        <div
          className="pdf-quote-highlight-layer"
          style={{ width: overlaySize.width, height: overlaySize.height }}
        >
          {scaledHighlights.map((h, i) => (
            <div
              key={i}
              className="pdf-quote-highlight-mark"
              style={{ left: h.left, top: h.top, width: h.width, height: h.height }}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
})

export function PdfQuoteHighlightViewer({
  blobUrl,
  pageNo = null,
  quote = null,
  className,
  minHeight = 'calc(100vh - 360px)',
}: PdfQuoteHighlightViewerProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const slotRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const [docLoading, setDocLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null)
  const [numPages, setNumPages] = useState(0)
  const [pageSize, setPageSize] = useState<PageSize | null>(null)
  const [locate, setLocate] = useState<LocateState | null>(null)
  const [locating, setLocating] = useState(false)
  const [visiblePages, setVisiblePages] = useState<Set<number>>(() => new Set())

  const markVisible = useCallback((pageNumber: number) => {
    setVisiblePages((prev) => {
      if (prev.has(pageNumber)) return prev
      const next = new Set(prev)
      next.add(pageNumber)
      return next
    })
  }, [])

  useEffect(() => {
    if (!blobUrl) {
      setPdf(null)
      setNumPages(0)
      setPageSize(null)
      setLocate(null)
      setError(null)
      setVisiblePages(new Set())
      return
    }

    let cancelled = false
    setDocLoading(true)
    setError(null)
    setPdf(null)
    setNumPages(0)
    setPageSize(null)
    setLocate(null)
    setVisiblePages(new Set())

    void (async () => {
      try {
        const doc = await getDocument(blobUrl).promise
        if (cancelled) return
        setPdf(doc)
        setNumPages(doc.numPages)

        const hint = pageNo && pageNo > 0 ? Math.min(pageNo, doc.numPages) : 1
        setVisiblePages(new Set([hint]))

        const first = await doc.getPage(hint)
        if (cancelled) return
        const vp = first.getViewport({ scale: RENDER_SCALE })
        setPageSize({ width: vp.width, height: vp.height })
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'PDF 加载失败')
        }
      } finally {
        if (!cancelled) setDocLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [blobUrl])

  useEffect(() => {
    if (!pdf) return
    let cancelled = false
    setLocating(Boolean(quote?.trim()))
    setLocate(null)

    void (async () => {
      try {
        const result = await locateQuoteInPdf(pdf, quote || '', pageNo, RENDER_SCALE)
        if (cancelled) return
        setLocate(result)
        markVisible(result.pageNumber)
      } catch {
        if (!cancelled) {
          const fallback = pageNo && pageNo > 0 ? Math.min(pageNo, pdf.numPages) : 1
          setLocate({ pageNumber: fallback, highlights: [], matched: false })
          markVisible(fallback)
        }
      } finally {
        if (!cancelled) setLocating(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [pdf, quote, pageNo, markVisible])

  useEffect(() => {
    if (!pdf || !numPages) return
    const root = scrollRef.current
    if (!root) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const pageNumber = Number(entry.target.getAttribute('data-pdf-page'))
          if (Number.isFinite(pageNumber) && pageNumber > 0) {
            markVisible(pageNumber)
          }
        }
      },
      { root, rootMargin: IO_ROOT_MARGIN, threshold: 0.01 },
    )

    slotRefs.current.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [pdf, numPages, markVisible])

  useEffect(() => {
    if (!locate?.pageNumber) return
    const el = slotRefs.current.get(locate.pageNumber)
    if (!el) return
    requestAnimationFrame(() => {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
    })
  }, [locate?.pageNumber, numPages])

  const highlightsForPage = useCallback(
    (pageNumber: number) => {
      if (locate?.pageNumber === pageNumber && locate.highlights.length) {
        return locate.highlights
      }
      return []
    },
    [locate],
  )

  if (!blobUrl) {
    return <Empty description="PDF 未加载" />
  }

  if (docLoading && !pdf) {
    return (
      <div className="pdf-quote-viewer-loading" style={{ minHeight }}>
        <Spin tip="打开 PDF…" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="pdf-quote-viewer-error" style={{ minHeight }}>
        <Empty description={error} />
      </div>
    )
  }

  if (!pdf || numPages < 1) {
    return (
      <div className="pdf-quote-viewer-error" style={{ minHeight }}>
        <Empty description="无法打开 PDF" />
      </div>
    )
  }

  const slotMinHeight = (pageSize?.height ?? 420) + 24
  const focusPage = locate?.pageNumber ?? (pageNo && pageNo > 0 ? pageNo : 1)

  return (
    <div
      className={className ? `pdf-quote-viewer ${className}` : 'pdf-quote-viewer'}
      style={{ minHeight }}
    >
      <div ref={scrollRef} className="pdf-quote-pages-scroll">
        {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNumber) => {
          const shouldRender = visiblePages.has(pageNumber)
          return (
            <div
              key={pageNumber}
              ref={(el) => {
                if (el) slotRefs.current.set(pageNumber, el)
                else slotRefs.current.delete(pageNumber)
              }}
              data-pdf-page={pageNumber}
              className={[
                'pdf-quote-page-slot',
                pageNumber === focusPage ? 'pdf-quote-page-slot-focus' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{ minHeight: slotMinHeight }}
            >
              {shouldRender ? (
                <PdfLazyPage
                  pdf={pdf}
                  pageNumber={pageNumber}
                  highlights={highlightsForPage(pageNumber)}
                />
              ) : (
                <div className="pdf-quote-page-placeholder">
                  <Typography.Text type="secondary">第 {pageNumber} 页</Typography.Text>
                </div>
              )}
            </div>
          )
        })}
      </div>
      {locating ? (
        <Typography.Text type="secondary" className="pdf-quote-viewer-tip">
          正在定位引用原文…（可先滚动浏览，共 {numPages} 页）
        </Typography.Text>
      ) : quote?.trim() && locate ? (
        locate.matched ? (
          <Typography.Text type="secondary" className="pdf-quote-viewer-tip">
            已在第 {locate.pageNumber} 页标红定位原文
            {pageNo != null && pageNo > 0 && pageNo !== locate.pageNumber
              ? `（估算页 ${pageNo}，实际匹配第 ${locate.pageNumber} 页）`
              : ''}
            {' · '}滚动时按可见范围加载页面（共 {numPages} 页）
          </Typography.Text>
        ) : (
          <Typography.Text type="warning" className="pdf-quote-viewer-tip">
            第 {locate.pageNumber} 页未匹配到引用原文（可能为扫描件或版式差异），请手动滚动查找 · 共{' '}
            {numPages} 页
          </Typography.Text>
        )
      ) : (
        <Typography.Text type="secondary" className="pdf-quote-viewer-tip">
          滚动浏览 PDF，进入可见区域后异步加载页面（共 {numPages} 页）
        </Typography.Text>
      )}
    </div>
  )
}
